import { inject, Injectable } from "@angular/core";
import { UserService } from "@main-services/user.service";
import { SavingsService } from "@main-services/savings.service";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { filter, map, skip, take } from "rxjs/operators";
import { WalletsFacade } from "@modules/+new/wallets/domain/+state/wallets.facade";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { StakingBannerFacade } from "@modules/staking/domain/+state/staking-banner.facade";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { TradablesFacade } from "@modules/+new/core/tradables/domain/+state/tradables.facade";
import { isDerivative } from "@modules/top-movers/domain/util/top-movers.util";
import { Helper } from "@helpers/functions";
import { MasterdataService } from "@core-services/masterdata.service";
import { PercentSymbolPipe } from "@modules/core/pipes/percent-symbol.pipe";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { BestFacade } from "@modules/new_account/features/my-best/domain/+state/best.facade";
import { LeveragedTokenWalletService } from "@main-services/wallet/leveraged_token_wallet.service";
import { LaunchpadFacade } from "@modules/launchpad/domain/+state/launchpad.facade";
import { config, ENVS } from "@config";
import { Dictionary } from "@helpers/types";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Web3Facade } from "@modules/web3/+state/web3.facade";

enum SegmentKeys {
	profileType = "profileType",
	profileStatus = "profileStatus",
	savingStatus = "savingStatus",
	cashPlusStatus = "cashPlusStatus",
	fiatWalletStatus = "walletStatus",
	assetWalletStatus = "assetWalletStatus",
	stakingStatus = "stakingStatus",
	assetsNotStaked = "assetsNotStaked",
	leverageWalletStatus = "leverageWalletStatus",
	launchpadNewsletterStatus = "launchpadNewsletterStatus",
	userPropertyData = "userPropertyData",
	userHasBestOrPan = "bestPanHoldings",
	userHasVision = "visionHoldings"
}

@Injectable({
	providedIn: "root"
})
export class BpStoriesFacade {
	// array with empty string otherwise user sees all stories
	private segments$: BehaviorSubject<Dictionary<string[]>> = new BehaviorSubject<Dictionary<string[]>>({
		[SegmentKeys.profileType]: [],
		[SegmentKeys.profileStatus]: [],
		[SegmentKeys.savingStatus]: [],
		[SegmentKeys.cashPlusStatus]: [],
		[SegmentKeys.fiatWalletStatus]: [],
		[SegmentKeys.assetWalletStatus]: [],
		[SegmentKeys.stakingStatus]: [],
		[SegmentKeys.assetsNotStaked]: [],
		[SegmentKeys.leverageWalletStatus]: [],
		[SegmentKeys.launchpadNewsletterStatus]: []
	});
	private bestFacade = inject(BestFacade);
	private userService = inject(UserService);
	private savingService = inject(SavingsService);
	private cashPlusFacade = inject(CashPlusFacade);
	private walletsFacade = inject(WalletsFacade);
	private stakingBannerFacade = inject(StakingBannerFacade);
	private portfolioFacade = inject(PortfolioFacade);
	private tradablesFacade = inject(TradablesFacade);
	private masterdataService = inject(MasterdataService);
	private percentSymbolPipe = inject(PercentSymbolPipe);
	private profileFacade = inject(NewProfileFacade);
	private leveragedTokenWalletService = inject(LeveragedTokenWalletService);
	private launchpadFacade = inject(LaunchpadFacade);
	private launchDarklyService = inject(LaunchDarklyService);
	private web3Facade = inject(Web3Facade);

	public getSegments$(): Observable<string[]> {
		this.loadSegments();
		return this.segments$.asObservable().pipe(map((segments) => Object.values(segments).flat()));
	}

	public loadSegments(): void {
		this.segments$.next({
			...this.segments$.getValue(),
			[SegmentKeys.profileType]: [this.getProfileType()],
			[SegmentKeys.profileStatus]: [this.getProfileStatus()]
		});
		this.getSavingStatus$();
		this.getCashPlusStatus$();
		this.getFiatWalletStatus$();
		this.getAssetWalletStatus$();
		this.getAssetsNotStaked$();
		this.getLeverageWalletStatus();
		this.getLaunchpadNewsletterStatus();
		this.getBestPanHoldingsLabels();
		this.hasVisionHoldingsLabels();
	}

	public getUserPropertyData(): Observable<object> {
		return combineLatest([
			this.getCoinMostHold$(),
			this.getStakingStatus$(),
			this.getCashPlusRate$(),
			this.profileFacade.getProfile$(),
			this.bestFacade.getBestState$()
		]).pipe(
			map(([coinMostHold, stakingStatus, cashPlusRate, profile, bestFacade]) => {
				const userPropertyData = {
					"{user_pid}": Helper.getCookie("pid"),
					"{user_language}": this.userService.user.getLanguage(),
					"{user_country}": this.getCountry(profile.country_id).toLowerCase()
				};
				let isUserVip: boolean;

				if (coinMostHold && coinMostHold.assetName) {
					userPropertyData["{top_not_staked_coin_name}"] = coinMostHold.assetName;
				}

				if (stakingStatus && stakingStatus.coinStakedName) {
					userPropertyData["{staked_coin_name}"] = stakingStatus.coinStakedName;
				}

				if (bestFacade && bestFacade.currentVipLevel.level) {
					userPropertyData["{best_level}"] = bestFacade.currentVipLevel.level;
					isUserVip = bestFacade.currentVipLevel.level > 0;
				}

				if (cashPlusRate) {
					cashPlusRate.forEach((rate) => {
						userPropertyData[`${rate.individualRateStatus}`] = this.percentSymbolPipe.transform(
							rate.individualRate
						);
						userPropertyData[`${rate.annualRateStatus}`] = this.percentSymbolPipe.transform(
							rate.annualRate
						);
					});
				}

				this.segments$.next({
					...this.segments$.getValue(),
					[SegmentKeys.userPropertyData]: [
						userPropertyData["{user_language}"],
						userPropertyData["{user_country}"],
						userPropertyData["{user_pid}"],
						isUserVip ? BestVipStatus.IsBestVip : BestVipStatus.IsNotBestVip,
						coinMostHold && coinMostHold.coinMostHold ? coinMostHold.coinMostHold : "",
						stakingStatus?.numberStakedCoins
					]
				});

				return userPropertyData;
			})
		);
	}

	private getProfileStatus(): ProfileStatus {
		if (this.userService.user.isVerified()) {
			return ProfileStatus.Verified;
		}

		if (this.userService.user.isVerificationPending()) {
			return ProfileStatus.Pending;
		}

		return ProfileStatus.Unverified;
	}

	private getProfileType(): ProfileType {
		if (this.userService.user.isBusinessUser()) {
			return ProfileType.Business;
		}
		return ProfileType.Regular;
	}

	private getSavingStatus$(): void {
		let allSavingStatus: SavingStatus[] = [];

		combineLatest([this.savingService.getList(), this.tradablesFacade.tradables$])
			.pipe(
				filter(([_, tradables]) => Object.keys(tradables).length > 0),
				take(1),
				map(([plans, tradables]) => {
					let activePlans = 0;
					let pausedPlans = 0;

					plans.forEach((plan) => {
						if (plan.isActive()) {
							activePlans++;
						}

						if (plan.isPaused()) {
							pausedPlans++;
						}
					});

					if (activePlans === 0) {
						allSavingStatus.push(SavingStatus.NoActiveSavingPlans);
					}

					if (activePlans === 1) {
						if (
							plans[0].getSavingPaymentAccount() &&
							isDerivative(tradables[plans[0].getCurrencyId()].assetGroupName)
						) {
							allSavingStatus.push(SavingStatus.OneActiveSecuritySavingPlan);
						} else {
							allSavingStatus.push(SavingStatus.OneActiveCryptoSavingPlan);
						}
					}

					if (activePlans > 1) {
						allSavingStatus.push(SavingStatus.MultipleSavingPlans);
					}

					if (pausedPlans > 0) {
						allSavingStatus.push(SavingStatus.PausedSavingPlans);
					}

					return allSavingStatus;
				})
			)
			.subscribe((savingStatus) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.savingStatus]: savingStatus })
			);
	}

	private getCashPlusStatus$(): void {
		this.cashPlusFacade
			.getSubscriptionsState$()
			.pipe(
				filter((state) => !!state),
				take(1),
				map((state) => {
					if (state.subscribed) {
						return CashPlusStatus.CashPlusEnabled;
					}
					return CashPlusStatus.CashPlusDisabled;
				})
			)
			.subscribe((cashPlusState) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.cashPlusStatus]: [cashPlusState] })
			);
	}

	private getCashPlusRate$(): Observable<
		{
			individualRateStatus: CashPlusStatus;
			individualRate: number;
			annualRateStatus: CashPlusStatus;
			annualRate: number;
		}[]
	> {
		// EUR, USD, CHF
		const fiatIdToCheck = ["1", "2", "4"];

		return combineLatest([
			this.cashPlusFacade.getCombinedCashPlusState$(),
			this.walletsFacade.getFiatWallets$()
		]).pipe(
			filter(([state, fiatWallets]) => !!state && fiatWallets.length > 0),
			map(([state, fiatWallets]) => {
				if (state) {
					const filteredFiatWallets = fiatWallets.filter((fiatWallet) =>
						fiatIdToCheck.includes(fiatWallet.fiatId.toString())
					);

					return filteredFiatWallets.map((fiatWallet) => {
						const configuration = state.configurations.configurations.find(
							(config) => +config.fiat_id === fiatWallet.fiatId
						);

						if (fiatWallet.fiatId === 1) {
							return {
								individualRateStatus: CashPlusStatus.CashPlusIndividualRateEur,
								individualRate: +configuration?.individual_annual_percentage_rate,
								annualRateStatus: CashPlusStatus.CashPlusRateEur,
								annualRate: +configuration?.annual_percentage_rate
							};
						}

						if (fiatWallet.fiatId === 2) {
							return {
								individualRateStatus: CashPlusStatus.CashPlusIndividualRateUsd,
								individualRate: +configuration?.individual_annual_percentage_rate,
								annualRateStatus: CashPlusStatus.CashPlusRateUsd,
								annualRate: +configuration?.annual_percentage_rate
							};
						}

						if (fiatWallet.fiatId === 4) {
							return {
								individualRateStatus: CashPlusStatus.CashPlusIndividualRateGbp,
								individualRate: +configuration?.individual_annual_percentage_rate,
								annualRateStatus: CashPlusStatus.CashPlusRateGbp,
								annualRate: +configuration?.annual_percentage_rate
							};
						}
					});
				}
				return [];
			})
		);
	}

	private getFiatWalletStatus$(): void {
		this.walletsFacade
			.getFiatWallets$()
			.pipe(
				map((fiatWallets) => {
					let walletSum = 0;

					if (fiatWallets.some((fiatWallet) => fiatWallet.balance > 0)) {
						return WalletStatus.PositiveFiatBalance;
					}

					fiatWallets.forEach((fiatWallet) => {
						return (walletSum = walletSum + fiatWallet.balance);
					});

					if (walletSum === 0) {
						return WalletStatus.ZeroFiatBalance;
					}
				}),
				take(1)
			)
			.subscribe((walletStatus) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.fiatWalletStatus]: [walletStatus] })
			);
	}

	private getAssetWalletStatus$(): void {
		this.portfolioFacade
			.getPortfolioPerformanceState$()
			.pipe(
				filter((data) => !data.isLoading && !!data && !!data.asset_group_performances),
				take(1),
				map((portfolioPerformance) => {
					let walletBalance = 0;

					portfolioPerformance.asset_group_performances.map((assetGroup) => {
						if (assetGroup.asset_group.toLowerCase() !== "fiat") {
							walletBalance = walletBalance + +assetGroup.fiat_balance;
						}
					});

					if (walletBalance > 0) {
						return WalletStatus.PositiveAssetBalance;
					}

					if (walletBalance === 0) {
						return WalletStatus.ZeroAssetBalance;
					}
				})
			)
			.subscribe((walletStatus) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.assetWalletStatus]: [walletStatus] })
			);
	}

	private getCoinMostHold$(): Observable<{ coinMostHold: StakingStatus; assetName: string }> {
		return combineLatest([
			this.walletsFacade.getCryptoWallets$(),
			// as the initial value is not a falsy value but an empty object, we need to skip the first emit
			this.stakingBannerFacade.getBannerStateData$().pipe(filter((data) => !!data && !!data.assetConfig)),
			this.portfolioFacade.getPortfolioOverviewState$().pipe(skip(1), take(1)),
			this.tradablesFacade.tradables$
		]).pipe(
			filter(([_, __, portfolio, tradables]) => Object.keys(tradables).length > 0 && !portfolio.isLoading),
			map(([assets, stakeables, portfolio, tradables]) => {
				const sortedAssets = assets.slice().sort((a, b) => b.balance - a.balance);

				let stakeableNotStakedInPortfolio = [];
				if (portfolio.portfolios?.length > 0) {
					portfolio.portfolios.forEach((asset) => {
						stakeables.assetConfig.assets.forEach((stakeable) => {
							if (
								stakeable &&
								asset &&
								asset.asset_type === "CRYPTOCOIN" &&
								stakeable.assetId === asset.asset_id &&
								stakeable.availability === "ENABLED" &&
								+asset.staked_balance_percent === 0
							) {
								return stakeableNotStakedInPortfolio.push(asset);
							}
						});
					});
				}

				const highestStakeableAsset = [];
				sortedAssets.find((asset) => {
					if (asset && asset.balance > 0) {
						const stakeableAsset = stakeables.assetConfig.assets.find(
							(stakeable) =>
								stakeable &&
								+stakeable.assetId === asset.cryptoCoinId &&
								stakeable.availability === "ENABLED"
						);
						if (stakeableAsset) {
							highestStakeableAsset.push(stakeableAsset);
						}

						return stakeableAsset && !stakeables.stakedItemsId.includes(stakeableAsset.assetId);
					}
				});

				if (highestStakeableAsset && stakeableNotStakedInPortfolio.length > 0) {
					let highestStakeableAssetName = tradables[+highestStakeableAsset[0].assetId].name;
					return { coinMostHold: StakingStatus.CoinMostHold, assetName: highestStakeableAssetName };
				}
				return { coinMostHold: null, assetName: null };
			})
		);
	}

	private getStakingStatus$(): Observable<{ numberStakedCoins: StakingStatus; coinStakedName?: string }> {
		return combineLatest([
			// as the initial value is not a falsy value but an empty object, we need to skip the first emit
			this.stakingBannerFacade.getBannerStateData$().pipe(skip(1)),
			this.tradablesFacade.tradables$
		]).pipe(
			filter(([_, tradables]) => Object.keys(tradables).length > 0),
			map(([stakeables, tradables]) => {
				if (stakeables.stakedItemsId.length === 0) {
					return { numberStakedCoins: StakingStatus.NoAssetStaked };
				}
				if (stakeables.stakedItemsId.length === 1) {
					const asset = tradables[+stakeables.stakedItemsId[0]];

					return { numberStakedCoins: StakingStatus.OneAssetStaked, coinStakedName: asset.name };
				}
				if (stakeables.stakedItemsId.length > 1) {
					return { numberStakedCoins: StakingStatus.MultipleAssetsStaked };
				}
			})
		);
	}

	private getAssetsNotStaked$(): void {
		let assetIdsToCheck: string[];

		let stakingStatuses: Record<string, StakingStatus>;
		if (this.isStaging()) {
			assetIdsToCheck = ["5", "22", "436", "51", "433", "31"];
			stakingStatuses = {
				"5": StakingStatus.EthereumNotStaked,
				"22": StakingStatus.AdaNotStaked,
				"436": StakingStatus.SolanaNotStaked,
				"51": StakingStatus.PolkadotNotStaked,
				"433": StakingStatus.PolygonNotStaked,
				"31": StakingStatus.TronNotStaked
			};
		} else {
			assetIdsToCheck = ["5", "22", "135", "51", "134", "31"];
			stakingStatuses = {
				"5": StakingStatus.EthereumNotStaked,
				"22": StakingStatus.AdaNotStaked,
				"135": StakingStatus.SolanaNotStaked,
				"51": StakingStatus.PolkadotNotStaked,
				"134": StakingStatus.PolygonNotStaked,
				"31": StakingStatus.TronNotStaked
			};
		}

		combineLatest([
			// as the initial value is not a falsy value but an empty object, we need to skip the first emit
			this.stakingBannerFacade.getBannerStateData$().pipe(take(1)),
			this.portfolioFacade.getPortfolioOverviewState$().pipe(take(1))
		])
			.pipe(
				filter(([_, portfolioPerformance]) => !portfolioPerformance.isLoading),
				map(([stakedAssets, portfolioPerformance]) => {
					const stakedAssetIds = stakedAssets.stakedItemsId;

					let notStakedAssetIds = [];
					if (portfolioPerformance.portfolios) {
						notStakedAssetIds = portfolioPerformance.portfolios
							.filter(
								(portfolio) =>
									assetIdsToCheck.includes(portfolio.asset_id) &&
									portfolio.asset_type === "CRYPTOCOIN"
							)
							.filter((portfolio) => !stakedAssetIds.includes(portfolio.asset_id));
					}

					if (notStakedAssetIds.length > 0) {
						return notStakedAssetIds
							.map((asset) => stakingStatuses[asset.asset_id])
							.filter((status) => !!status);
					}

					return [];
				})
			)
			.subscribe((statuses) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.assetsNotStaked]: statuses })
			);
	}

	private getLeverageWalletStatus(): void {
		this.leveragedTokenWalletService
			.hasWallets$()
			.pipe(
				map((value) =>
					value ? LeverageWalletStatus.HasLeverageWallet : LeverageWalletStatus.HasNoLeverageWallet
				),
				take(1)
			)
			.subscribe((status) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.leverageWalletStatus]: [status] })
			);
	}

	private getLaunchpadNewsletterStatus(): void {
		this.launchpadFacade
			.getNewsletterSub$()
			.pipe(
				map((sub) => (sub.is_subscribed ? SpotlightNewsletterSubscribed : null)),
				filter((status) => status !== null),
				take(1)
			)
			.subscribe((status) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.launchpadNewsletterStatus]: [status] })
			);
	}

	private getBestPanHoldingsLabels(): void {
		combineLatest({
			assetBalances: this.web3Facade.getPantosAndBestBalance$(),
			bestPid: this.launchDarklyService.getFeatureConfig$<{ pid: string }>(LD_FLAG.BEST_PID),
			pantosPid: this.launchDarklyService.getFeatureConfig$<{ pid: string }>(LD_FLAG.PANTOS_PID)
		})
			.pipe(
				// filter when both balances are zero
				filter(({ assetBalances }) => assetBalances.some((asset) => asset.balance > 0)),
				map(({ assetBalances, bestPid, pantosPid }) => {
					let labels = [];
					assetBalances.forEach((asset) => {
						if (asset.balance > 0) {
							labels.push(
								asset.asset.getPid() === bestPid.pid
									? HasBestOrPanStatus.HasBest
									: HasBestOrPanStatus.HasPan
							);
						}
					});
					if (labels.length > 0) {
						return labels;
					}
					return null;
				}),
				filter((status) => !!status),
				take(1)
			)
			.subscribe((status) =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.userHasBestOrPan]: status })
			);
	}

	private hasVisionHoldingsLabels(): void {
		this.web3Facade
			.userHoldsVision()
			.pipe(
				filter((hasVision) => hasVision),
				take(1)
			)
			.subscribe(() =>
				this.segments$.next({ ...this.segments$.getValue(), [SegmentKeys.userHasVision]: [userHasVision] })
			);
	}

	private getCountry(countryCode: string): string {
		return this.masterdataService.getCountryById(countryCode)?.getName();
	}

	private isStaging(): boolean {
		return config.envName === ENVS.STAGING || config.envName === ENVS.LOCAL;
	}
}

enum ProfileStatus {
	Unverified = "user_unverified",
	Verified = "user_verified",
	Pending = "user_verification_pending"
}

enum ProfileType {
	Regular = "user_type_regular",
	Business = "user_type_business"
}

enum SavingStatus {
	NoActiveSavingPlans = "savings_no_plans",
	OneActiveCryptoSavingPlan = "savings_one_active_plan_crypto",
	OneActiveSecuritySavingPlan = "savings_one_active_plan_security",
	MultipleSavingPlans = "savings_has_multiple_plans",
	PausedSavingPlans = "savings_paused_plans"
}

enum CashPlusStatus {
	CashPlusEnabled = "cashplus_enabled",
	CashPlusDisabled = "cashplus_disabled",
	CashPlusRateEur = "cashplus_rate_eur",
	CashPlusRateUsd = "cashplus_rate_usd",
	CashPlusRateGbp = "cashplus_rate_gbp",
	CashPlusIndividualRateEur = "cashplus_individual_rate_eur",
	CashPlusIndividualRateUsd = "cashplus_individual_rate_usd",
	CashPlusIndividualRateGbp = "cashplus_individual_rate_gbp"
}

enum StakingStatus {
	NoAssetStaked = "staking_no_assets_staked",
	OneAssetStaked = "staking_one_asset_staked",
	MultipleAssetsStaked = "staking_multiple_assets_staked",
	CoinMostHold = "staking_holds_cryptocoin_not_staked",
	EthereumNotStaked = "staking_holds_eth_not_staked",
	AdaNotStaked = "staking_holds_ada_not_staked",
	SolanaNotStaked = "staking_holds_sol_not_staked",
	PolkadotNotStaked = "staking_holds_dot_not_staked",
	PolygonNotStaked = "staking_holds_matic_not_staked",
	TronNotStaked = "staking_holds_trx_not_staked"
}

enum WalletStatus {
	ZeroAssetBalance = "zero_asset_balance",
	ZeroFiatBalance = "zero_fiat_balance",
	PositiveAssetBalance = "positive_asset_balance",
	PositiveFiatBalance = "positive_fiat_balance"
}

enum LeverageWalletStatus {
	HasLeverageWallet = "has_leverage_wallet",
	HasNoLeverageWallet = "no_leverage_wallet"
}

enum BestVipStatus {
	IsBestVip = "user_best_vip",
	IsNotBestVip = "user_not_best_vip"
}

enum HasBestOrPanStatus {
	HasBest = "user_best_holder",
	HasPan = "user_pan_holder"
}

const userHasVision: string = "user_vision_holder";

const SpotlightNewsletterSubscribed: string = "spotlight_newsletter_subscribed";
